import * as THREE from 'three';
import { FORMATIONS_GLSL } from './formationsGLSL';

const FORMATION_COUNT = 14;

/**
 * WebGL2 GPGPU particle field — Immersiom-formation port of the WebGPU
 * `ParticleSystem`.
 *
 * State lives in THREE RGBA32F textures rotated each step. The pair
 * `(prevIdx, nowIdx)` is sampled and the next position is written into
 * `writeIdx`, which is never one of the sampled slots — otherwise WebGL
 * raises a framebuffer-vs-texture feedback loop error and the draw is
 * dropped. After writing we rotate: `(prev, now, write) <- (now, write, prev)`.
 */
export class ParticleSystemGL {
  readonly mesh: THREE.Mesh;
  readonly count: number;
  readonly texSize: number;

  private posTargets: [THREE.WebGLRenderTarget, THREE.WebGLRenderTarget, THREE.WebGLRenderTarget];
  private prevIdx = 0;
  private nowIdx = 1;
  private writeIdx = 2;

  private fsScene = new THREE.Scene();
  private fsCam = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
  private fsQuad: THREE.Mesh;

  private initMat: THREE.ShaderMaterial;
  private updateMat: THREE.ShaderMaterial;
  private renderMat: THREE.ShaderMaterial;

  // Audio + state uniforms shared between update and render.
  private uTime = { value: 0 };
  private uDt = { value: 1 / 60 };
  private uBass = { value: 0 };
  private uMid = { value: 0 };
  private uTreble = { value: 0 };
  private uBeat = { value: 0 };
  private uFormationA = { value: 0 };
  private uFormationB = { value: 0 };
  private uFormationMix = { value: 0 };
  private uBaseR = { value: 8.0 };
  private uHue = { value: 28 / 360 };
  private uSaturation = { value: 0.9 };
  private uIntensity = { value: 1 };
  private uWarmth = { value: 0.62 };

  // Crossfade controller state.
  private currentFormation = 0;
  private crossfadeFrom = 0;
  private crossfadeTo = 0;
  private crossfadeT = 1;
  private crossfadeDuration = 1.4;

  constructor(private renderer: THREE.WebGLRenderer, count: number) {
    this.count = count;
    this.texSize = Math.ceil(Math.sqrt(count));

    // We need RGBA32F render targets. WebGL2 + EXT_color_buffer_float is
    // ubiquitous on desktop/mobile; we don't gate on it because three.js
    // surfaces a clear error if the extension is missing.
    const targetOpts: THREE.RenderTargetOptions = {
      type: THREE.FloatType,
      format: THREE.RGBAFormat,
      minFilter: THREE.NearestFilter,
      magFilter: THREE.NearestFilter,
      wrapS: THREE.ClampToEdgeWrapping,
      wrapT: THREE.ClampToEdgeWrapping,
      depthBuffer: false,
      stencilBuffer: false,
      generateMipmaps: false,
    };
    this.posTargets = [
      new THREE.WebGLRenderTarget(this.texSize, this.texSize, targetOpts),
      new THREE.WebGLRenderTarget(this.texSize, this.texSize, targetOpts),
      new THREE.WebGLRenderTarget(this.texSize, this.texSize, targetOpts),
    ];

    this.initMat = this.buildInitMaterial();
    this.updateMat = this.buildUpdateMaterial();
    this.renderMat = this.buildRenderMaterial();

    this.fsQuad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), this.initMat);
    this.fsScene.add(this.fsQuad);

    this.mesh = this.buildBillboards();
  }

  init(): void {
    this.fsQuad.material = this.initMat;
    // Seed all three slots with the same Fibonacci rest position so the
    // implied velocity at t=0 is zero.
    for (let i = 0; i < 3; i++) {
      this.renderer.setRenderTarget(this.posTargets[i]);
      this.renderer.render(this.fsScene, this.fsCam);
    }
    this.renderer.setRenderTarget(null);
    // Point the render material at the freshest history pair (matches the
    // post-step state we'd land in after one cycle).
    this.renderMat.uniforms.uPosTex.value = this.posTargets[this.nowIdx].texture;
    this.renderMat.uniforms.uPosPrevTex.value = this.posTargets[this.prevIdx].texture;
  }

  step(dt: number, time: number): void {
    // Fixed substep — keeps the spring stable even when the host loop
    // hiccups. Matches the WebGPU engine's behavior.
    this.uTime.value = time;
    this.uDt.value = 1 / 60;

    // Bind: read prev + now, write into the disjoint third slot. The
    // disjoint write is what avoids the feedback-loop GL error.
    this.updateMat.uniforms.uPosPrev.value = this.posTargets[this.prevIdx].texture;
    this.updateMat.uniforms.uPosNow.value = this.posTargets[this.nowIdx].texture;

    this.fsQuad.material = this.updateMat;
    this.renderer.setRenderTarget(this.posTargets[this.writeIdx]);
    this.renderer.render(this.fsScene, this.fsCam);
    this.renderer.setRenderTarget(null);

    // Rotate (prev, now, write) <- (now, write, prev).
    const newPrev = this.nowIdx;
    const newNow = this.writeIdx;
    const newWrite = this.prevIdx;
    this.prevIdx = newPrev;
    this.nowIdx = newNow;
    this.writeIdx = newWrite;

    // Wire the render mesh to the new textures.
    this.renderMat.uniforms.uPosTex.value = this.posTargets[this.nowIdx].texture;
    this.renderMat.uniforms.uPosPrevTex.value = this.posTargets[this.prevIdx].texture;

    void dt;
  }

  setAudio(bass: number, mid: number, treble: number, beat: number): void {
    this.uBass.value = bass;
    this.uMid.value = mid;
    this.uTreble.value = treble;
    this.uBeat.value = beat;
  }

  setFormation(id: number, durationSec = 1.4): void {
    const target = ((id % FORMATION_COUNT) + FORMATION_COUNT) % FORMATION_COUNT;
    if (target === this.currentFormation && this.crossfadeT >= 1) return;
    this.crossfadeFrom = this.currentFormation;
    this.crossfadeTo = target;
    this.crossfadeT = 0;
    this.crossfadeDuration = Math.max(0.05, durationSec);
    this.uFormationA.value = this.crossfadeFrom;
    this.uFormationB.value = this.crossfadeTo;
    this.uFormationMix.value = 0;
    this.currentFormation = target;
  }

  getFormation(): number {
    return this.currentFormation;
  }

  advanceCrossfade(dt: number): void {
    if (this.crossfadeT >= 1) {
      this.uFormationA.value = this.currentFormation;
      this.uFormationB.value = this.currentFormation;
      this.uFormationMix.value = 0;
      return;
    }
    this.crossfadeT += dt / this.crossfadeDuration;
    if (this.crossfadeT >= 1) {
      this.crossfadeT = 1;
      this.uFormationA.value = this.currentFormation;
      this.uFormationB.value = this.currentFormation;
      this.uFormationMix.value = 0;
    } else {
      const x = this.crossfadeT;
      this.uFormationMix.value = x * x * (3 - 2 * x); // smoothstep
    }
  }

  setBaseRadius(r: number): void {
    this.uBaseR.value = r;
  }

  setViewportHeight(h: number): void {
    this.renderMat.uniforms.uViewportHeight.value = h;
  }

  setPointSize(size: number): void {
    this.renderMat.uniforms.uPointSize.value = size;
  }

  setAlpha(a: number): void {
    this.renderMat.uniforms.uBaseAlpha.value = a;
  }

  setVisual(hue: number, saturation: number, intensity: number, warmth: number): void {
    this.uHue.value = (((hue % 360) + 360) % 360) / 360;
    this.uSaturation.value = THREE.MathUtils.clamp(saturation, 0, 1);
    this.uIntensity.value = THREE.MathUtils.clamp(intensity, 0, 4);
    this.uWarmth.value = THREE.MathUtils.clamp(warmth, 0, 1);
  }

  setStiffness(k: number): void {
    this.updateMat.uniforms.uStiff.value = k;
  }

  setDamping(d: number): void {
    this.updateMat.uniforms.uDamp.value = d;
  }

  dispose(): void {
    this.posTargets[0].dispose();
    this.posTargets[1].dispose();
    this.posTargets[2].dispose();
    this.initMat.dispose();
    this.updateMat.dispose();
    this.renderMat.dispose();
    (this.fsQuad.geometry as THREE.BufferGeometry).dispose();
    (this.mesh.geometry as THREE.BufferGeometry).dispose();
  }

  // ---------- internals ----------------------------------------------------

  private buildInitMaterial(): THREE.ShaderMaterial {
    const vertex = /* glsl */ `
      out vec2 vUv;
      void main() {
        vUv = uv;
        gl_Position = vec4(position.xy, 0.0, 1.0);
      }
    `;
    const fragment = /* glsl */ `
      precision highp float;
      in vec2 vUv;
      out vec4 outColor;
      uniform float uSize;
      uniform float uCount;
      uniform float uInvCount;
      uniform float uBaseR;
      ${FORMATIONS_GLSL}
      void main() {
        float px = floor(vUv.x * uSize);
        float py = floor(vUv.y * uSize);
        float i = py * uSize + px;
        if (i >= uCount) { outColor = vec4(0.0, 0.0, 0.0, 1.0); return; }
        vec3 rest = fibonacciRest(i, uInvCount);
        outColor = vec4(rest * uBaseR, 1.0);
      }
    `;
    return new THREE.ShaderMaterial({
      glslVersion: THREE.GLSL3,
      vertexShader: vertex,
      fragmentShader: fragment,
      uniforms: {
        uSize: { value: this.texSize },
        uCount: { value: this.count },
        uInvCount: { value: 1 / Math.max(1, this.count - 1) },
        uBaseR: this.uBaseR,
      },
    });
  }

  private buildUpdateMaterial(): THREE.ShaderMaterial {
    const vertex = /* glsl */ `
      out vec2 vUv;
      void main() {
        vUv = uv;
        gl_Position = vec4(position.xy, 0.0, 1.0);
      }
    `;
    const fragment = /* glsl */ `
      precision highp float;
      in vec2 vUv;
      out vec4 outColor;
      uniform sampler2D uPosPrev;
      uniform sampler2D uPosNow;
      uniform float uSize;
      uniform float uCount;
      uniform float uInvCount;
      uniform float uBaseR;
      uniform float uTime;
      uniform float uDt;
      uniform float uBass;
      uniform float uMid;
      uniform float uTreble;
      uniform float uBeat;
      uniform int uFormationA;
      uniform int uFormationB;
      uniform float uFormationMix;
      uniform float uStiff;
      uniform float uDamp;
      ${FORMATIONS_GLSL}
      void main() {
        float px = floor(vUv.x * uSize);
        float py = floor(vUv.y * uSize);
        float i = py * uSize + px;
        if (i >= uCount) { outColor = vec4(0.0, 0.0, 0.0, 1.0); return; }

        vec3 rest = fibonacciRest(i, uInvCount);
        vec3 prev = texture(uPosPrev, vUv).rgb;
        vec3 now = texture(uPosNow, vUv).rgb;

        vec3 tA = dispatchFormation(uFormationA, rest, uTime, uBaseR, uBass, uMid, uTreble, uBeat);
        vec3 target;
        if (uFormationA == uFormationB) {
          target = tA;
        } else {
          vec3 tB = dispatchFormation(uFormationB, rest, uTime, uBaseR, uBass, uMid, uTreble, uBeat);
          target = mix(tA, tB, uFormationMix);
        }

        vec3 vel = (now - prev) / uDt;
        vec3 accel = (target - now) * uStiff - vel * uDamp;
        vec3 velNext = vel + accel * uDt;
        vec3 posNext = now + velNext * uDt;
        outColor = vec4(posNext, 1.0);
      }
    `;
    return new THREE.ShaderMaterial({
      glslVersion: THREE.GLSL3,
      vertexShader: vertex,
      fragmentShader: fragment,
      uniforms: {
        uPosPrev: { value: null },
        uPosNow: { value: null },
        uSize: { value: this.texSize },
        uCount: { value: this.count },
        uInvCount: { value: 1 / Math.max(1, this.count - 1) },
        uBaseR: this.uBaseR,
        uTime: this.uTime,
        uDt: this.uDt,
        uBass: this.uBass,
        uMid: this.uMid,
        uTreble: this.uTreble,
        uBeat: this.uBeat,
        uFormationA: this.uFormationA,
        uFormationB: this.uFormationB,
        uFormationMix: this.uFormationMix,
        uStiff: { value: 28.0 },
        uDamp: { value: 10.5 },
      },
    });
  }

  private buildRenderMaterial(): THREE.ShaderMaterial {
    const vertex = /* glsl */ `
      in vec2 aRef;
      uniform sampler2D uPosTex;
      uniform sampler2D uPosPrevTex;
      uniform float uPointSize;
      uniform float uDt;
      uniform float uBeat;
      out float vSpeed;
      out float vSeed;
      out vec2 vSpriteUv;
      void main() {
        vec3 pos = texture(uPosTex, aRef).rgb;
        vec3 posPrev = texture(uPosPrevTex, aRef).rgb;
        vSpeed = length(pos - posPrev);
        vSeed = fract(aRef.x * 73.13 + aRef.y * 191.71);
        vSpriteUv = uv;
        vec4 mv = modelViewMatrix * vec4(pos, 1.0);
        float speedScaled = vSpeed / max(uDt, 0.001);
        float sparkle = pow(sin(vSeed * 91.7 + speedScaled * 0.08) * 0.5 + 0.5, 5.0);
        float billboardSize = uPointSize * (5.1 + min(2.0, speedScaled * 0.03) + sparkle * 1.7 + uBeat * 1.05);
        mv.xy += position.xy * billboardSize;
        gl_Position = projectionMatrix * mv;
      }
    `;
    const fragment = /* glsl */ `
      precision highp float;
      in float vSpeed;
      in float vSeed;
      in vec2 vSpriteUv;
      out vec4 fragColor;
      uniform float uBass;
      uniform float uMid;
      uniform float uTreble;
      uniform float uBeat;
      uniform float uDt;
      uniform float uBaseAlpha;
      uniform float uHue;
      uniform float uSaturation;
      uniform float uIntensity;
      uniform float uWarmth;
      vec3 hsv(vec3 c) {
        vec3 rgb = clamp(abs(mod(c.x * 6.0 + vec3(0.0, 4.0, 2.0), 6.0) - 3.0) - 1.0, 0.0, 1.0);
        return c.z * mix(vec3(1.0), rgb, c.y);
      }
      void main() {
        vec2 c = vSpriteUv - 0.5;
        float d2 = dot(c, c);
        if (d2 > 0.25) discard;
        float core = pow(smoothstep(0.25, 0.0, d2), 2.1);
        float halo = exp(-d2 * 11.0) * 0.18;
        float speedScaled = vSpeed / max(uDt, 0.001);
        float sparkle = pow(sin(vSeed * 91.7 + speedScaled * 0.08) * 0.5 + 0.5, 5.0);
        float heat = clamp(uWarmth * 0.62 + speedScaled * 0.08 + uBass * 0.14 + uBeat * 0.14, 0.0, 1.0);
        vec3 cool = hsv(vec3(fract(uHue + 0.52 + vSeed * 0.025), 0.72 * uSaturation, 0.58));
        vec3 warm = hsv(vec3(fract(uHue + 0.015), 0.94 * uSaturation, 0.68));
        vec3 tint = mix(cool, warm, heat);
        vec3 hot = hsv(vec3(fract(uHue + 0.035), 0.72 * uSaturation, 1.0));
        tint = mix(tint, hot, sparkle * (0.3 + uTreble * 0.16));
        float brightness = 0.72 + uIntensity * 0.48 + core * 0.82 + uMid * 0.08 + uBeat * 0.16;
        float alpha = (core + halo + sparkle * 0.2) * uBaseAlpha * (0.92 + uBass * 0.1);
        fragColor = vec4(min(tint * brightness, vec3(2.4)), alpha);
      }
    `;
    return new THREE.ShaderMaterial({
      glslVersion: THREE.GLSL3,
      vertexShader: vertex,
      fragmentShader: fragment,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      uniforms: {
        uPosTex: { value: null },
        uPosPrevTex: { value: null },
        uPointSize: { value: 0.03 },
        uViewportHeight: { value: 800 },
        uDt: this.uDt,
        uBass: this.uBass,
        uMid: this.uMid,
        uTreble: this.uTreble,
        uBeat: this.uBeat,
        uBaseAlpha: { value: 0.42 },
        uHue: this.uHue,
        uSaturation: this.uSaturation,
        uIntensity: this.uIntensity,
        uWarmth: this.uWarmth,
      },
      side: THREE.DoubleSide,
    });
  }

  private buildBillboards(): THREE.Mesh {
    const plane = new THREE.PlaneGeometry(1, 1);
    const geom = new THREE.InstancedBufferGeometry();
    geom.index = plane.index?.clone() ?? null;
    geom.setAttribute('position', plane.getAttribute('position').clone());
    geom.setAttribute('uv', plane.getAttribute('uv').clone());
    const refs = new Float32Array(this.count * 2);
    const halfTexel = 0.5 / this.texSize;
    for (let i = 0; i < this.count; i++) {
      const px = i % this.texSize;
      const py = Math.floor(i / this.texSize);
      refs[i * 2 + 0] = px / this.texSize + halfTexel;
      refs[i * 2 + 1] = py / this.texSize + halfTexel;
    }
    geom.setAttribute('aRef', new THREE.InstancedBufferAttribute(refs, 2));
    geom.instanceCount = this.count;
    geom.boundingSphere = new THREE.Sphere(new THREE.Vector3(0, 0, 0), 100);
    plane.dispose();

    const mesh = new THREE.Mesh(geom, this.renderMat);
    mesh.frustumCulled = false;
    return mesh;
  }
}
